<template>
  <div v-if="visible" class="fu-drn" data-test="fu-drawer-next-date">
    <span class="fu-drn__label" :id="`${uid}-label`">Next follow-up</span>

    <div v-if="!pickerShown" class="fu-drn__auto" data-test="fu-drawer-next-auto">
      <fu-icon name="cal" :size="16" />
      <span class="fu-drn__date" :aria-labelledby="`${uid}-label`">{{ officeLabel || date }}</span>
      <span class="fu-drn__why">{{ why }}</span>
      <button
        v-if="canOverride"
        type="button"
        class="fu-drn__override"
        title="Pick a different date than your office setting"
        data-test="fu-drawer-override"
        @click="$emit('override')"
      >Override</button>
    </div>

    <template v-else>
      <div class="fu-drn__presets" role="group" aria-label="Quick picks">
        <button
          v-for="p in presets"
          :key="p.key"
          type="button"
          class="fu-drn__preset"
          :class="{ 'fu-drn__preset--on': p.date === date }"
          :aria-pressed="String(p.date === date)"
          :data-test="`fu-drawer-preset-${p.key}`"
          @click="$emit('change', p.date)"
        >{{ p.label }}</button>
      </div>
      <div class="fu-drn__row">
        <input
          type="date"
          class="fu-drn__input"
          :min="today"
          :value="date || ''"
          :aria-labelledby="`${uid}-label`"
          :aria-invalid="date ? 'false' : 'true'"
          data-test="fu-drawer-date-input"
          @change="onDate($event.target.value)"
        >
        <button
          v-if="overridden && !editable"
          type="button"
          class="fu-drn__office"
          data-test="fu-drawer-use-office"
          @click="$emit('use-office')"
        >Use office setting</button>
      </div>
      <p v-if="overridden && !editable" class="fu-drn__note" data-test="fu-drawer-override-note">{{ overrideNote }}</p>
      <p v-else-if="!date" class="fu-drn__note fu-drn__note--warn" data-test="fu-drawer-date-missing">Pick the next follow-up date before saving.</p>
    </template>
  </div>
</template>

<script>
/**
 * DrawerNextDate — the next follow-up date (Nilay 2026-10-02: today's rules, the design's look),
 * driven by drawerNextDate:
 *   • the office-frequency date read-only, "in N days · office setting", and Override where today
 *     allows it (canOverride — never on claims / outstanding pre-auths / balances);
 *   • after Override, or on the money queues (editable), a native date input (min = today) with the
 *     design's presets; "Use office setting" undoes an Override, with the design's overridden note;
 *   • hidden when there is no next date (the last attempt, an outcome that ends follow-ups);
 *   • no office date (a settings read that fails today → legacyError) → the input, so it can be picked.
 *
 *   visible, date ('YYYY-MM-DD' | null), editable, canOverride, overridden, today ('YYYY-MM-DD'),
 *   officeLabel (the date, formatted by the container), frequency ({ count, unit } | null)
 * Events: override, use-office, change('YYYY-MM-DD'). Presentational.
 */
import FuIcon from '../FuIcon.vue';
import { datePresets, daysFromToday } from '@/utils/followups/drawer/drawerNextDate.js';

let seq = 0;

export default {
  name: 'DrawerNextDate',
  components: { FuIcon },
  props: {
    visible: { type: Boolean, default: true },
    date: { type: String, default: null },
    editable: { type: Boolean, default: false },
    canOverride: { type: Boolean, default: false },
    overridden: { type: Boolean, default: false },
    today: { type: String, required: true },
    officeLabel: { type: String, default: '' },
    frequency: { type: Object, default: null },
  },
  data() {
    seq += 1;
    return { uid: `fu-drnext-${seq}` };
  },
  computed: {
    pickerShown() {
      return this.editable || this.overridden || !this.date;
    },
    presets() {
      return datePresets(this.today);
    },
    why() {
      const n = daysFromToday(this.date, this.today);
      if (n === null) return 'office setting';
      if (n === 0) return 'today · office setting';
      return `in ${n} ${n === 1 ? 'day' : 'days'} · office setting`;
    },
    overrideNote() {
      const f = this.frequency;
      const every = f && f.count !== undefined && f.unit ? `every ${f.count} ${String(f.unit).toLowerCase()}` : 'its schedule';
      return `Overrides your office setting of ${every}. The Follow-up column will flag it as overridden.`;
    },
  },
  methods: {
    onDate(v) {
      if (!v || v < this.today) return; // the native min is advisory on some browsers
      this.$emit('change', v);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drn__label { @include fu-dr-field-label; }

.fu-drn__auto {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  min-width: 0;
  padding: 0 12px;
  border: 1px solid $fu-border;
  border-radius: $fu-radius-md;
  background: $fu-neutral-25;
  .fu-icon { flex: none; color: $fu-text-subtle; }
}

.fu-drn__date { font-size: 14px; font-weight: $fu-fw-bold; white-space: nowrap; }

.fu-drn__why {
  @include fu-ellipsis;
  min-width: 0;
  font-size: 12.5px;
  color: $fu-text-muted;
}

.fu-drn__override {
  @include fu-link-btn;
  margin-left: auto;
  font-size: 12.5px;
  font-weight: $fu-fw-bold;
  white-space: nowrap;
}

.fu-drn__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.fu-drn__preset {
  @include fu-chip(false);
  padding: 5px 10px;
  font-size: 12.5px;
}

.fu-drn__preset--on {
  border-color: $fu-brand;
  background: $fu-brand-50;
  color: $fu-brand-800;
  &:hover { background: $fu-brand-50; color: $fu-brand-800; }
}

.fu-drn__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.fu-drn__input {
  @include fu-input(40px);
  width: 200px;
  max-width: 100%;
}

.fu-dr--phone .fu-drn__input { @include fu-dr-phone-input; }

// Phone: "in 7 days · office setting" got cut to "in …". The line wraps instead — the date and
// Override on the first row, the reason on its own row under the date (never ellipsized).
.fu-dr--phone .fu-drn__auto {
  flex-wrap: wrap;
  row-gap: 2px;
  padding: 8px 12px;
}
.fu-dr--phone .fu-drn__date { min-width: 0; white-space: normal; }
.fu-dr--phone .fu-drn__why {
  order: 3;
  flex: 1 0 100%;
  padding-left: 26px; // under the date: the 16px icon + the 10px gap
  overflow: visible;
  white-space: normal;
  text-overflow: clip;
}

.fu-drn__office {
  @include fu-link-btn;
  font-size: 12.5px;
  font-weight: $fu-fw-bold;
  color: $fu-text-muted;
}

.fu-drn__note { @include fu-dr-hint; }
.fu-drn__note--warn { color: $fu-warning-text; font-weight: $fu-fw-semibold; }
</style>
